// Partner cockpit — cross-tenant portfolio dashboard (B5B-PARTNER-COCKPIT §2). KPI stat row across the
// firm's mandated tenants + a colour-banded tenant grid (red <60 / amber 60–79 / green ≥80). Data comes
// from the partner-cockpit-worker, scoped server-side to the consultant's mandate. Behind real auth.
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { LayoutGrid, Ticket, CalendarClock, TriangleAlert, ArrowRight } from "lucide-react";
import { cockpit, CockpitError, type TenantKpi } from "../lib/cockpit";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function Kpi({ icon, label, value, loading }: { icon: React.ReactNode; label: string; value: number; loading?: boolean }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <span className="text-muted-foreground" aria-hidden>{icon}</span>
      </CardHeader>
      <CardContent>{loading ? <Skeleton className="h-8 w-12" /> : <div className="text-3xl font-semibold tracking-tight">{value}</div>}</CardContent>
    </Card>
  );
}

const BAND_CLASS: Record<TenantKpi["band"], string> = {
  green: "text-emerald-600", amber: "text-amber-600", red: "text-destructive", none: "text-muted-foreground",
};

function TenantCard({ t }: { t: TenantKpi }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="truncate">{t.tenantName}</span>
          {t.mandateEndingSoon && <Badge variant="secondary" className="shrink-0">mandate ending</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-baseline gap-2">
          <span className={`text-3xl font-semibold ${BAND_CLASS[t.band]}`}>{t.score ?? "—"}</span>
          <span className="text-xs text-muted-foreground">compliance score</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div><div className="font-medium">{t.openTickets}</div><div className="text-xs text-muted-foreground">open</div></div>
          <div><div className="font-medium">{t.dueSoon}</div><div className="text-xs text-muted-foreground">due ≤7d</div></div>
          <div><div className="font-medium">{t.programs}</div><div className="text-xs text-muted-foreground">programs</div></div>
        </div>
        <Button asChild size="sm" variant="outline" className="mt-1 w-fit">
          <Link to="/tenant/$tenantId" params={{ tenantId: t.tenantId }}>View tenant <ArrowRight className="size-4" /></Link>
        </Button>
      </CardContent>
    </Card>
  );
}

export function PortfolioRoute() {
  const { data, isLoading, error } = useQuery({ queryKey: ["cockpit-portfolio"], queryFn: () => cockpit.portfolio(), retry: false });
  const tot = data?.totals;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Portfolio" description={data?.firmName ? `${data.firmName} — across ${tot?.tenants ?? 0} mandated tenants` : "Your consultancy portfolio across all mandated tenants"} />

      {error instanceof CockpitError && error.status === 401 && (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Sign in as a firm consultant to view your cross-tenant portfolio.</CardContent></Card>
      )}
      {error && !(error instanceof CockpitError && error.status === 401) && (
        <Card><CardContent className="py-8 text-center text-sm text-destructive">{(error as Error).message}</CardContent></Card>
      )}

      {!error && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi icon={<LayoutGrid className="size-4" />} label="Tenants in mandate" value={tot?.tenants ?? 0} loading={isLoading} />
            <Kpi icon={<Ticket className="size-4" />} label="Open tickets" value={tot?.openTickets ?? 0} loading={isLoading} />
            <Kpi icon={<CalendarClock className="size-4" />} label="Due within 7 days" value={tot?.dueSoon ?? 0} loading={isLoading} />
            <Kpi icon={<TriangleAlert className="size-4" />} label="Mandates ending ≤30d" value={tot?.mandatesEndingSoon ?? 0} loading={isLoading} />
          </div>

          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-40" />)}</div>
          ) : data && data.tenants.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{data.tenants.map((t) => <TenantCard key={t.tenantId} t={t} />)}</div>
          ) : (
            <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No tenants in your mandate yet. A tenant engages your firm via their Implementatiepartner settings.</CardContent></Card>
          )}
        </>
      )}
    </div>
  );
}
